import React from 'react';
import { Card } from './components/ui/card';

function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-[#FBFBF9] py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <Card className="bg-white p-8 sm:p-12 border border-[#E6E6E6] rounded-2xl">
          <h1 className="text-4xl font-serif lowercase text-[#3B3A39] mb-2"
              style={{ fontFamily: "'DM Serif Display', ui-serif, Georgia, serif" }}>
            privacy policy
          </h1>
          <p className="text-sm text-[#8B8A88] mb-8">Last updated: August 2026</p>

          <div className="prose prose-lg max-w-none space-y-8">
            
            {/* Simple Intro */}
            <div>
              <h2 className="text-2xl font-semibold text-[#3B3A39] mb-3">the short version</h2>
              <p className="text-[#6B6A68] leading-relaxed">
                Your phone number stays private. We never sell it. We never share it with recruiters. 
                We use it only to operate HeyTraveler, text you approved matches with consent, and respond to your messages.
              </p>
            </div>

            {/* What We Collect */}
            <div>
              <h2 className="text-2xl font-semibold text-[#3B3A39] mb-3">what we collect</h2>
              <ul className="list-disc pl-6 space-y-2 text-[#6B6A68]">
                <li><strong>Phone number</strong> - only if you give it to us</li>
                <li><strong>Name</strong> - optional, if you share it in conversation</li>
                <li><strong>Job preferences</strong> - specialty, location, pay requirements, start date</li>
                <li><strong>Conversation history</strong> - our chat messages so we remember what you're looking for</li>
                <li><strong>Technical data</strong> - device type, browser info (standard web stuff)</li>
              </ul>
            </div>

            {/* How We Use It */}
            <div>
              <h2 className="text-2xl font-semibold text-[#3B3A39] mb-3">how we use your info</h2>
              <ul className="list-disc pl-6 space-y-2 text-[#6B6A68]">
                <li><strong>Find you jobs</strong> - match your preferences to available positions</li>
                <li><strong>Text you matches</strong> - send SMS when we find jobs you'd like</li>
                <li><strong>Respond to you</strong> - answer your questions via web chat or SMS</li>
                <li><strong>Improve the service</strong> - understand what nurses are looking for</li>
              </ul>
              
              <p className="text-[#6B6A68] leading-relaxed mt-4">
                <strong>We do NOT:</strong> Sell your data. Share your number with recruiters. 
                Send you spam. Track you across other websites. Build creepy profiles.
              </p>
            </div>

            {/* SMS Terms */}
            <div className="bg-[#F5F0EB] p-6 rounded-xl">
              <h2 className="text-2xl font-semibold text-[#3B3A39] mb-3">sms / text messaging terms</h2>
              
              <div className="space-y-4 text-[#6B6A68]">
                <p>
                  <strong>What you're agreeing to:</strong><br/>
                  You opt in by checking the explicit SMS consent box on the web or by texting <strong>START</strong>.
                  Giving us a phone number by itself does not turn on proactive alerts.
                </p>

                <p>
                  <strong>What you'll get:</strong><br/>
                  Strict matches from the approved Care Team Solutions inventory, with no more than one proactive alert
                  per 24 hours. You'll also get direct responses when you text us questions.
                </p>

                <p>
                  <strong>Opt-out anytime:</strong><br/>
                  Reply <strong>STOP</strong> to unsubscribe. You'll get one confirmation text, then we won't 
                  message you again unless you text <strong>START</strong> to opt back in.
                </p>

                <p>
                  <strong>Help:</strong><br/>
                  Reply <strong>HELP</strong> or email support@staffingreferrals.com
                </p>

                <p>
                  <strong>Costs:</strong><br/>
                  Message and data rates may apply based on your carrier. We don't charge you — 
                  your phone company might.
                </p>

                <p>
                  <strong>You don't have to opt in:</strong><br/>
                  You can use the web chat instead. SMS is optional.
                </p>
              </div>
            </div>

            {/* Data Sharing */}
            <div>
              <h2 className="text-2xl font-semibold text-[#3B3A39] mb-3">who we share data with</h2>
              <ul className="list-disc pl-6 space-y-2 text-[#6B6A68]">
                <li><strong>Staffing agencies</strong> - the alpha does not automatically share your contact information.
                    Asking to apply creates a HeyTraveler human-review request only</li>
                <li><strong>Service providers</strong> - Twilio (for SMS), MongoDB (database), 
                    Anthropic (AI), PostHog (pseudonymous product events), Sentry (redacted errors),
                    and hosting providers - only what's needed to run the service</li>
                <li><strong>Nobody else</strong> - we don't sell data, we don't run ads, 
                    we don't give your info to random recruiters</li>
              </ul>
            </div>

            {/* Data Security */}
            <div>
              <h2 className="text-2xl font-semibold text-[#3B3A39] mb-3">how we protect your data</h2>
              <p className="text-[#6B6A68] leading-relaxed">
                We use industry-standard security (encryption, secure servers, access controls). 
                But honestly, no system is 100% secure. We do our best to protect your info, 
                but we can't guarantee absolute security.
              </p>
            </div>

            {/* Your Rights */}
            <div>
              <h2 className="text-2xl font-semibold text-[#3B3A39] mb-3">your rights</h2>
              <ul className="list-disc pl-6 space-y-2 text-[#6B6A68]">
                <li><strong>Access:</strong> Ask what data we have about you</li>
                <li><strong>Delete:</strong> Request we delete your data</li>
                <li><strong>Opt-out:</strong> Stop SMS anytime (reply STOP)</li>
                <li><strong>Correct:</strong> Update your preferences or info</li>
              </ul>
              <p className="text-[#6B6A68] mt-4">
                Email us at: <a href="mailto:support@staffingreferrals.com" className="text-[#3B3A39] underline">support@staffingreferrals.com</a>
              </p>
            </div>

            {/* Data Retention */}
            <div>
              <h2 className="text-2xl font-semibold text-[#3B3A39] mb-3">how long we keep data</h2>
              <p className="text-[#6B6A68] leading-relaxed">
                As long as you're using the service, we keep your data to provide the service. 
                If you opt out or request deletion, we remove your data within 30 days 
                (except what we're legally required to keep for compliance).
              </p>
            </div>

            {/* Children */}
            <div>
              <h2 className="text-2xl font-semibold text-[#3B3A39] mb-3">age requirement</h2>
              <p className="text-[#6B6A68] leading-relaxed">
                You must be 18+ to use HeyTraveler.
                We don't knowingly collect data from children.
              </p>
            </div>

            {/* Changes */}
            <div>
              <h2 className="text-2xl font-semibold text-[#3B3A39] mb-3">changes to this policy</h2>
              <p className="text-[#6B6A68] leading-relaxed">
                If we change this policy, we'll update the date at the top and notify you 
                (via SMS or email if we have significant changes).
              </p>
            </div>

            {/* Contact */}
            <div className="bg-[#F5F0EB] p-6 rounded-xl">
              <h2 className="text-2xl font-semibold text-[#3B3A39] mb-3">questions?</h2>
              <p className="text-[#6B6A68]">
                Email us: <a href="mailto:support@staffingreferrals.com" className="text-[#3B3A39] underline font-medium">support@staffingreferrals.com</a>
              </p>
              <p className="text-[#6B6A68] mt-2">
                Text us: <a href="sms:+18447476718" className="text-[#3B3A39] underline font-medium">+1 (844) 747-6718</a>
              </p>
            </div>

            {/* Legal Entity */}
            <div className="text-sm text-[#8B8A88] border-t border-[#E6E6E6] pt-6 mt-8">
              <p>
                HeyTraveler is operated by Staffing Referrals<br/>
                Alpha inventory is limited to HeyTraveler-approved Care Team Solutions opportunities supplied through Staffing Referrals.<br/>
                For legal inquiries: support@staffingreferrals.com
              </p>
            </div>

          </div>
        </Card>

        {/* Back Link */}
        <div className="text-center mt-8">
          <a href="/" className="text-[#6B6A68] hover:text-[#3B3A39] text-sm">
            ← back to heytraveler
          </a>
        </div>
      </div>
    </div>
  );
}

export default PrivacyPolicy;
